import React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Box, Tooltip } from '@mui/material';

export default function CRMTable({ rows, loading, error, onRowClick }: { rows?: any[]; loading?: boolean; error?: string | null; onRowClick?: (row: any) => void }) {
    const data = Array.isArray(rows) ? rows : [];

    if (!data || data.length === 0) {
        return (
            <Box style={{ padding: 16 }}>No investors found.</Box>
        );
    }

    const allKeys = Object.keys(data[0]);
    const visibleKeys = allKeys.filter((k) => {
        const lower = String(k).toLowerCase();
        if (lower === 'id') return false;
        if (lower.endsWith('_id')) return false;
        if (['personid', 'person_id', 'user_id', 'investor_id'].includes(lower)) return false;
        return true;
    });
    const columns = visibleKeys.slice(0, 8);

    const emailKey = allKeys.find((k) => k.toLowerCase() === 'email' || k.toLowerCase().includes('email'));
    const nameKey = allKeys.find((k) => ['name', 'full_name', 'display_name'].includes(k.toLowerCase()))
        || (allKeys.includes('first_name') && allKeys.includes('last_name') ? 'first_name' : undefined as any);
    const linkedinKey = allKeys.find((k) => k.toLowerCase().includes('linkedin'));

    const idCandidates = ['id', 'person_id', 'personid', 'user_id', 'investor_id'];
    const remaining = columns.filter((c) => {
        const lower = String(c).toLowerCase();
        if (lower === 'last_name') return false;
        if (emailKey && c === emailKey) return false;
        if (nameKey && c === nameKey) return false;
        if (idCandidates.includes(lower)) return false;
        return true;
    });

    const renderColumns: string[] = [];
    if (nameKey) renderColumns.push(nameKey);
    if (emailKey) renderColumns.push(emailKey);
    for (const k of remaining) renderColumns.push(k);

    const summaryKey = allKeys.find((k) => /investor\s*summary/i.test(k) || (/investor/i.test(k) && /summary/i.test(k)));

    return (
        <Table className='elegant-table users-rounded-table no-borders'>
            <TableHead>
                <TableRow>
                    {renderColumns.map((c) => (
                        <TableCell key={c} style={{ fontWeight: 700 }}>
                            {c === nameKey ? 'Full Name' : c === emailKey ? 'Email' : c}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((r, idx) => {
                    const summaryText = summaryKey ? (r[summaryKey] ?? '') : '';
                    return (
                        <Tooltip key={r.id ?? idx} title={String(summaryText)} placement='top' arrow disableHoverListener={!summaryText}>
                            <TableRow
                                hover
                                onClick={() => {
                                    console.log('CRMTable row clicked', { idx, row: r });
                                    console.log('CRMTable id candidates', {
                                        id: r.id,
                                        investor_id: r.Investor_id,
                                        user_id: r.user_id,
                                        company_id: r.company_id,
                                    });
                                    if (onRowClick) onRowClick(r);
                                }}
                                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                            >
                                {renderColumns.map((c) => {
                                    const val = r[c];

                                    if (nameKey && c === nameKey) {
                                        let full = '';
                                        if (nameKey === 'first_name') {
                                            const first = String(r['first_name'] ?? '').trim();
                                            const last = String(r['last_name'] ?? '').trim();
                                            full = `${first} ${last}`.trim();
                                        } else {
                                            full = String(r[nameKey] ?? '').trim();
                                        }
                                        return <TableCell key={c}>{full || '—'}</TableCell>;
                                    }

                                    if (emailKey && c === emailKey) {
                                        const mail = String(r[emailKey] ?? '').trim();
                                        if (!mail) return <TableCell key={c}>—</TableCell>;
                                        return (
                                            <TableCell key={c}>
                                                <a href={`mailto:${mail}`}>{mail}</a>
                                            </TableCell>
                                        );
                                    }

                                    if (linkedinKey && c === linkedinKey) {
                                        const href = String(val ?? '').trim();
                                        if (!href) return <TableCell key={c}>—</TableCell>;
                                        const url = href.startsWith('http') ? href : `https://${href}`;
                                        return (
                                            <TableCell key={c}>
                                                <a href={url} target='_blank' rel='noopener noreferrer'>{href}</a>
                                            </TableCell>
                                        );
                                    }

                                    return <TableCell key={c}>{val === null || typeof val === 'undefined' ? '—' : String(val)}</TableCell>;
                                })}
                            </TableRow>
                        </Tooltip>
                    );
                })}
            </TableBody>
        </Table>
    );
}
